import React, { useContext, useEffect, useState } from 'react';

import { Locale } from 'date-fns';

import { LANGUAGES, LOCALES } from 'state/intl/types';
import en from 'state/translations/en.json';
import { regionEnum } from 'utils/environment';
import { Region } from 'utils/environment/types';
import { ISOs } from 'utils/form/constants';
import { getLanguage, getLocale } from 'utils/intl/language';
import { getMessagesForLanguage } from 'utils/intl/language/get-messages-for-language';
import { polyfillListFormatLocale } from 'utils/intl/polyfills/polyfill-list-format-locale';
import polyfillPluralRules from 'utils/intl/polyfills/polyfill-plural-rules-locale';
import logger, { addLoggingContext } from 'utils/logger';
import { useMemoAll } from 'utils/use-memo-all';

import { PROD_DEFAULT_REGION as DEFAULT_REGION, LOCALE_DATA } from './constants';
import { syncTranslationState } from './translations';

export type SupportedReadableLanguages = {
  en?: string;
  es?: string;
  fr?: string;
};

export const defaultLocale = 'en-US';

export interface IIntlProviderContext {
  region: Region;
  feCountryCode: ISOs;
  language: LANGUAGES;
  locale: string;
  dateFormat: Locale;
  messagesLoaded: boolean;
}

export const IntlContext = React.createContext<IIntlProviderContext>({} as IIntlProviderContext);

// These don't change over app life, calling setLanguageAndRestart will restart the app
const region = regionEnum;
const language = getLanguage();
const locale = getLocale();

export const useLocale = () => useContext(IntlContext);

// NOTE: cypress-v2 test suite requirement
// Skip react-intl error logging when running in cypress
// const skipErrorLogging = Boolean(isTest || window.Cypress);

addLoggingContext({ page_locale: locale });

const messagesPromise = getMessagesForLanguage(language, region).catch(error => {
  logger.error({ message: 'Error getting messages for language', error });
  return en;
});

export function IntlProvider({ children }: { children: React.ReactNode }) {
  const { dateFormat } = LOCALE_DATA[locale] ?? LOCALE_DATA[LOCALES.en_us];
  const [messagesLoaded, setMessagesLoaded] = useState(false);

  const value = useMemoAll({
    region,
    feCountryCode: ISOs[region] || ISOs[DEFAULT_REGION],
    language,
    locale,
    dateFormat,
    messagesLoaded,
  });

  useEffect(() => {
    polyfillListFormatLocale(locale as LOCALES);
    polyfillPluralRules(locale as LOCALES);
    messagesPromise.then(newMessages => {
      // Sync before setting state to ensure consistency during the next render
      syncTranslationState(locale, newMessages as any);
      setMessagesLoaded(true);
    });
  }, []);

  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
}

export default IntlContext.Consumer;
